<script setup>
import { useGlobalStore } from '@/stores/global';
import SocialsList from '@/components/SocialsList.vue';
defineProps({
  name: {
    type: String,
    default: () => '',
  },
  marker: {
    type: Object,
    default: () => {},
  },
  address: {
    type: String,
    default: () => '',
  },
  phone: {
    type: String,
    default: () => String,
  },
  time: {
    type: String,
    default: () => '',
  },
  brands: {
    type: Array,
    default: () => [],
  },
  fuel: {
    type: Array,
    default: () => [],
  },
  services: {
    type: Array,
    default: () => [],
  },
  link: {
    type: String,
    default: () => '',
  },
  id: {
    type: [String, Number],
    default: () => '',
  },
  brandsText: {
    type: String,
    default: () => '',
  },
  chooseAutocenterText: {
    type: String,
    default: () => '',
  },
  leaveRequestText: {
    type: String,
    default: () => '',
  },
  fuelText: {
    type: String,
    default: () => '',
  },
  servicesText: {
    type: String,
    default: () => '',
  },
  type: {
    type: String,
    default: () => '',
  },
  service: {
    type: String,
    default: () => '',
  },
  note: {
    type: String,
    default: () => '',
  },
  email: {
    type: String,
    default: () => '',
  },
  socials: {
    type: Array,
    default: () => [],
  },
  button: {
    type: Object,
    default: () => {},
  },
});

const globalStore = useGlobalStore();
function handleApplicationClick(type, id) {
  globalStore.setApplicationPopup({ visible: true, type: type, centerId: id });
}
const getDirectionsUrl = (marker) => {
  if (!marker || !marker.lat || !marker.lng) return '#';
  return `https://www.google.com/maps/dir/?api=1&destination=${marker.lat},${marker.lng}`;
};
</script>
<template>
  <div
    v-if="
      (name && name.length > 0) ||
      (address && address.length > 0) ||
      (phone && phone.length > 0) ||
      (time && time.length > 0) ||
      (brands && brands.length > 0) ||
      (fuel && fuel.length > 0) ||
      (services && services.length > 0) ||
      (link && link.length > 0) ||
      (service && service.length > 0) ||
      (note && note.length > 0) ||
      (email && email.length > 0) ||
      (socials && socials.length > 0) ||
      (button && Object.keys(button).length !== 0)
    "
    :class="[
      'mappoint',
      type === 'one_point' ? 'mappoint--one-point' : '',
      type === 'autocenter' ? 'mappoint--autocenter' : '',
    ]"
  >
    <template v-if="type === 'one_point'">
      <p v-if="name" class="mb-0 mappoint__title h4" v-html="name"></p>
      <div class="mappoint__divider"></div>
      <div v-if="address || phone || time || email" class="mappoint__info">
        <div class="mappoint__address body-3-medium" v-html="address"></div>
        <div v-if="phone || time" class="mappoint__contacts">
          <p
            v-if="phone"
            class="mb-0 mappoint__phone body-3-regular"
            v-html="phone"
          ></p>
          <p
            v-if="time"
            class="mb-0 mappoint__schedule body-3-regular"
            v-html="time"
          ></p>
        </div>
        <div v-if="email" class="mappoint__emails">
          <p class="mb-0 body-3-regular">
            <template v-for="emailItem in email" :key="emailItem">
              <a
                :href="`mailto:${emailItem}`"
                class="mappoint__email body-3-medium"
                v-html="emailItem"
              ></a>
            </template>
          </p>
        </div>
        <SocialsList
          v-if="socials && socials.length > 0"
          :data="socials"
          :view="'black'"
          class="mappoint__socials"
        />
      </div>
      <div class="mappoint__bottom">
        <div
          v-if="note && note.length > 0"
          class="mappoint__note body-3-regular"
          v-html="note"
        ></div>
        <Button v-slot="slotProps" as-child rounded :variant="'outlined'">
          <CTagDetect
            :to="getDirectionsUrl(marker)"
            target="_blank"
            :class="[slotProps.class]"
            class="mappoint__button button-black link-normal js-button-map-point"
          >
            {{ $t('networkMap.get_directions') }}
          </CTagDetect>
        </Button>
      </div>
    </template>
    <template v-else>
      <div class="mappoint__top">
        <p v-if="name" :class="['mb-0 mappoint__title h4']" v-html="name"></p>
        <div v-if="address || phone || time || email" class="mappoint__info">
          <div class="mappoint__address body-3-medium" v-html="address"></div>
          <div v-if="phone || time" class="mappoint__contacts">
            <p
              v-if="phone"
              class="mb-0 mappoint__phone body-3-regular"
              v-html="phone"
            ></p>
            <p
              v-if="time"
              class="mb-0 mappoint__schedule body-3-regular"
              v-html="time"
            ></p>
          </div>
          <div v-if="email" class="mappoint__email">
            <p class="mb-0 body-3-regular">
              <template v-for="emailItem in email" :key="emailItem">
                <a
                  :href="`mailto:${emailItem}`"
                  class="mappoint__email body-3-medium"
                  v-html="emailItem"
                ></a>
              </template>
            </p>
          </div>
          <SocialsList
            v-if="socials && socials.length > 0"
            :data="socials"
            :view="'black'"
            class="mappoint__socials"
          />
        </div>
        <div
          v-if="
            (brands && brands.length > 0) ||
            (fuel && fuel.length > 0) ||
            (services && services.length > 0)
          "
          class="mappoint__list body-3-regular"
        >
          <div v-if="brands && brands.length > 0" class="mappoint__brands">
            <p
              v-if="brandsText"
              class="mb-0 mappoint__brands-title"
              v-html="brandsText"
            ></p>
            <ul class="m-0 p-0 mappoint__brands-items">
              <li
                v-for="(brand, brandIndex) in brands"
                :key="brandIndex"
                v-tooltip.top="{
                  value: brand.alt,
                  class: 'mappoint__brands-item-tooltip',
                }"
                class="m-0 mappoint__brands-item"
              >
                <NuxtImg
                  v-if="brand?.src"
                  :src="brand.src"
                  :alt="brand.alt"
                  :height="brand.height"
                  :width="brand.width"
                  class="mappoint__brands-item-image"
                  loading="lazy"
                />
              </li>
            </ul>
          </div>
          <div v-if="fuel && fuel.length > 0" class="mappoint__fuel">
            <p
              v-if="fuelText"
              class="mb-0 mappoint__fuel-title"
              v-html="fuelText"
            ></p>
            <ul class="m-0 p-0 mappoint__fuel-items">
              <li
                v-for="(fuelItem, fuelIndex) in fuel"
                :key="fuelIndex"
                class="m-0 mappoint__fuel-item"
              >
                <span v-if="fuelItem?.title" v-html="fuelItem.title"></span>
              </li>
            </ul>
          </div>
          <div
            v-if="services && services.length > 0"
            class="mappoint__services"
          >
            <p
              v-if="servicesText"
              class="mb-0 mappoint__services-title"
              v-html="servicesText"
            ></p>
            <ul
              class="m-0 p-0 mappoint__services-items"
              :class="[
                type === 'autocenter'
                  ? 'mappoint__services-items--autocenter'
                  : '',
              ]"
            >
              <li
                v-for="(serviceItem, serviceIndex) in services"
                :key="serviceIndex"
                v-tooltip.top="{
                  value: type === 'autocenter' && serviceItem?.icon?.src ? serviceItem.title : '',
                  class: 'mappoint__services-item-tooltip',
                }"
                class="m-0 mappoint__services-item"
              >
                <template v-if="type === 'fuelstation_list'">
                  <NuxtImg
                    v-if="serviceItem?.icon && serviceItem?.icon?.src"
                    :src="serviceItem?.icon?.src"
                    :alt="serviceItem?.icon?.alt"
                    :height="serviceItem?.icon?.height"
                    :width="serviceItem?.icon?.width"
                    class="mappoint__services-item-icon"
                    loading="lazy"
                  />
                </template>
                <template v-else-if="type === 'autocenter'">
                  <NuxtImg
                    v-if="serviceItem?.icon?.src"
                    :src="serviceItem?.icon?.src"
                    :alt="serviceItem?.title"
                    height="40"
                    width="40"
                    class="mappoint__services-item-icon"
                    loading="lazy"
                  />
                  <span v-else v-html="serviceItem?.title"></span>
                </template>
                <template v-else>
                  <NuxtImg
                    v-if="serviceItem?.icon"
                    :src="serviceItem?.icon"
                    :alt="serviceItem?.title"
                    height="40"
                    width="40"
                    class="mappoint__services-item-icon"
                    loading="lazy"
                  />
                </template>
                <span
                  v-if="type !== 'autocenter' && serviceItem?.title"
                  v-html="serviceItem.title"
                ></span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-if="type === 'autocenter'" class="mappoint__bottom">
        <Button
          v-if="link && chooseAutocenterText"
          v-slot="slotProps"
          as-child
          rounded
          :variant="'outlined'"
        >
          <CTagDetect
            :to="link"
            target="_blank"
            :class="[slotProps.class]"
            class="mappoint__button button-black link-normal js-button-map-autocenter"
          >
            {{ chooseAutocenterText }}
          </CTagDetect>
        </Button>
        <Button
          v-if="id && leaveRequestText"
          class="mappoint__button js-button-map-request"
          rounded
          variant="text"
          @click="handleApplicationClick('autocenter_application', id)"
        >
          {{ leaveRequestText }}
        </Button>
      </div>
      <div v-if="type === 'service'" class="mappoint__bottom">
        <Button
          v-if="id && leaveRequestText"
          class="mappoint__button button-black link-normal js-button-map-service"
          rounded
          variant="outlined"
          @click="handleApplicationClick(service, id)"
        >
          {{ leaveRequestText }}
        </Button>
      </div>
      <div v-if="type === 'horeca_list'" class="mappoint__bottom">
        <Button
          v-if="button && button.url"
          v-slot="slotProps"
          as-child
          rounded
          :variant="'outlined'"
        >
          <CTagDetect
            :to="button?.url"
            target="_blank"
            :class="[slotProps.class]"
            class="mappoint__button button-black link-normal js-button-map-horeca"
          >
            {{ button?.title ? button?.title : button?.url }}
          </CTagDetect>
        </Button>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
.mappoint {
  $parent: &;
  align-content: flex-start;
  background-color: $white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 24px 16px;
  height: 100%;
  @include media-breakpoint-up(sm) {
    gap: 40px;
    padding: 32px 32px 16px;
  }
  @include media-breakpoint-up(md) {
    padding: 32px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(gap, 40);
    @include responsive-spacing(padding, 32);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__phone {
    &:deep(a) {
      @include body-3-medium;
    }
  }
  &__title {
    margin-bottom: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-bottom, 8);
    }
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__list {
    border-top: 1px solid $divider;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding-top: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(padding-top, 24);
    }
  }
  &__schedule {
    color: $on-surface-variant-2;
  }
  &__note {
    color: $on-surface-variant-2;
  }
  &__brands {
    display: flex;
    gap: 8px;
    flex-direction: column;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-title {
      color: $on-surface-variant-2;
    }
    &-items {
      column-gap: 16px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      list-style: none;
      row-gap: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 4);
      }
    }
    &-item {
      width: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 40);
      }
      &-image {
        aspect-ratio: 1/1;
        height: auto;
        width: 100%;
      }
    }
  }
  &__fuel {
    display: flex;
    gap: 8px;
    flex-direction: column;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-title {
      color: $on-surface-variant-2;
    }
    &-items {
      column-gap: 4px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      list-style: none;
      row-gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 4);
        @include responsive-spacing(row-gap, 8);
      }
    }
    &-item {
      align-items: center;
      background-color: $gray;
      border-radius: 4px;
      color: $black;
      display: flex;
      justify-content: center;
      min-height: 32px;
      min-width: 48px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
        @include responsive-spacing(min-height, 32);
        @include responsive-spacing(min-width, 48);
      }
    }
  }
  &__services {
    display: flex;
    gap: 8px;
    flex-direction: column;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-title {
      color: $on-surface-variant-2;
    }
    &-items {
      column-gap: 16px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      list-style: none;
      row-gap: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 4);
      }
      &--autocenter {
        column-gap: 12px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(column-gap, 12);
        }
        #{$parent} {
          &__services-item-icon {
            height: 40px;
            width: 40px;
            @include media-breakpoint-up(xl) {
              @include responsive-spacing(height, 40);
              @include responsive-spacing(width, 40);
            }
          }
        }
      }
    }
    &-item {
      align-items: center;
      color: $black;
      column-gap: 12px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      list-style: none;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 12);
      }
      &-icon {
        height: 24px;
        width: 24px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(height, 24);
          @include responsive-spacing(width, 24);
        }
      }
    }
  }
  &__bottom {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: auto;
    @include media-breakpoint-up(md) {
      grid-template-columns: auto auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &--one-point {
    align-content: start;
    display: grid;
    grid-template-areas: 'title' 'info' 'line' 'bottom';
    padding-bottom: 24px;
    @include media-breakpoint-up(sm) {
      column-gap: 24px;
      grid-template-columns: 6fr 1px 6fr;
      grid-template-areas: 'title . .' 'info line bottom';
      row-gap: 32px;
      padding-bottom: 32px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr;
      grid-template-areas: 'title' 'info' 'bottom';
      grid-template-rows: auto auto 1fr;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 24);
      @include responsive-spacing(row-gap, 32);
      @include responsive-spacing(padding-bottom, 32);
    }
    #{$parent} {
      &__divider {
        grid-area: line;
        background-color: $divider;
        display: block;
        height: 1px;
        width: 100%;
        @include media-breakpoint-up(sm) {
          height: 100%;
          width: 1px;
        }
        @include media-breakpoint-up(md) {
          display: none;
        }
      }
      &__title {
        grid-area: title;
        @include h3;
        @include media-breakpoint-up(sm) {
          padding-right: 24px;
          @include h4();
        }
        @include media-breakpoint-up(md) {
          padding-right: 0;
        }
      }
      &__info {
        grid-area: info;
        @include media-breakpoint-up(md) {
          align-self: end;
          padding-left: 0;
          margin-top: auto;
        }
      }
      &__bottom {
        grid-area: bottom;
        @include media-breakpoint-up(md) {
          border-top: 1px solid $divider;
          grid-template-columns: 1fr;
          padding-top: 24px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-top-width, 1);
          @include responsive-spacing(padding-top, 24);
        }
      }
      &__button {
        width: 100%;
        @include media-breakpoint-up(sm) {
          max-width: fit-content;
        }
      }
    }
  }
}
</style>
